import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Page-level hook that handles pending "download all charts" requests
 * Monitors URL search params for downloadAll=true and executes the download
 */
export const usePageDownloadHandler = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasDownloaded = useRef(false);
  const currentUrl = useRef<string>('');

  useEffect(() => {
    const url = window.location.href;
    
    // Reset download flag if URL has changed (user navigated to a different page)
    if (currentUrl.current !== url) {
      hasDownloaded.current = false;
      currentUrl.current = url;
    }

    if (searchParams.get('downloadAll') === 'true' && !hasDownloaded.current) {
      // Prevent multiple downloads
      hasDownloaded.current = true;
      
      // Remove the parameter from URL first
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('downloadAll');
        return newParams;
      });

      // Wait for charts to be fully rendered before attempting download
      const executeDownloadWithDelay = async () => {
        // Wait a bit for page to fully load and charts to render
        await new Promise(resolve => setTimeout(resolve, 300));
        await downloadAllSectionsOnPage();
      };

      executeDownloadWithDelay();
    }
  }, [searchParams, setSearchParams]);
};

/**
 * Utility function to download all chart sections present on the current page
 * Uses the exact same downloadChartSection function that logged-in users get
 */
const downloadAllSectionsOnPage = async (): Promise<void> => {
  try {
    // Import the exact same download function that chart cards use
    const { downloadChartSection } = await import('@/utils/chartDownload');

    // Define the sections to check for and download (same as chart cards)
    const sections = [
      { id: 'peer-comparison-charts-section', filename: 'peer-comparison-charts' },
      { id: 'snapshot-charts-section', filename: 'snapshot-charts' },
      { id: 'overtime-charts-section', filename: 'overtime-charts' }
    ];

    // Download each visible section sequentially (same as chart card downloadSectionAsImage)
    for (const section of sections) {
      const sectionElement = document.getElementById(section.id);
      if (sectionElement) {
        console.log(`Downloading section: ${section.id}`);
        try {
          // Use the exact same function call as chart cards
          await downloadChartSection(sectionElement, section.filename);
          // Small delay between section downloads
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (error) {
          console.error(`Error downloading section ${section.id}:`, error);
          // Continue with next section even if one fails
        }
      }
    }

    console.log('All sections downloaded successfully');
  } catch (error) {
    console.error('Error downloading sections:', error);
  }
}; 